import { Button, Dropdown, Tooltip } from "antd"
import type { MenuProps } from "antd"
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  DownOutlined,
} from "@ant-design/icons"
import { useNavigate } from "react-router-dom"
import { useActions } from "../../../hooks/useActions"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { CountryCode } from "../../../types/amazonAds"
import { getAvailableCountryCodes } from "../../../constants/country.constants"
import { LocalStorageKeys } from "../../../types/localStorage"
import styles from "./TopBar.module.scss"

interface TopBarProps {
  collapsed: boolean
  onToggle: () => void
}

const MARKETPLACE_LABEL: Record<string, string> = {
  US: CountryCode.US,
  CA: CountryCode.CA,
  MX: CountryCode.MX,
}

const marketplaceLabel = (code: string) =>
  MARKETPLACE_LABEL[code] ? `${code} — ${MARKETPLACE_LABEL[code]}` : code

const TopBar: React.FC<TopBarProps> = ({ collapsed, onToggle }) => {
  const navigate = useNavigate()
  const { setCountryCode } = useActions()
  const { countryCode } = useTypedSelector((state) => state.filter)

  const marketplaces = getAvailableCountryCodes()
  const storedMarketplace = [countryCode, localStorage.getItem(LocalStorageKeys.COUNTRY_CODE)].find(
    (code) => code && marketplaces.includes(code)
  )
  const currentMarketplace =
    storedMarketplace || (marketplaces.includes("US") ? "US" : marketplaces[0])

  const ToggleIcon = collapsed ? MenuUnfoldOutlined : MenuFoldOutlined
  const username = localStorage.getItem("userName") || "Username"

  const marketplaceMenu: MenuProps = {
    items: marketplaces.map((mp) => ({
      key: mp,
      label: marketplaceLabel(mp),
    })),
    selectable: true,
    selectedKeys: [currentMarketplace],
    onClick: ({ key }) => setCountryCode(key as string),
  }

  const profileMenu: MenuProps = {
    items: [
      {
        key: "logout",
        label: "Log out",
        icon: <LogoutOutlined />,
        danger: true,
      },
    ],
    onClick: ({ key }) => {
      if (key === "logout") logOutHandler()
    },
  }

  const logOutHandler = () => {
    localStorage.removeItem(LocalStorageKeys.SELLER_TOKEN)
    localStorage.removeItem("profileId")
    localStorage.removeItem("fromDate")
    localStorage.removeItem("toDate")
    localStorage.removeItem("userName")
    localStorage.removeItem("radio")
    localStorage.removeItem("perPage")
    localStorage.removeItem("selectedItem")
    navigate("/login")
  }

  return (
    <header className={styles.topbar}>
      <div className={styles.left}>
        <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"} mouseEnterDelay={0.4}>
          <Button
            type="text"
            className={styles.iconButton}
            onClick={onToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            icon={<ToggleIcon />}
          />
        </Tooltip>
      </div>

      <div className={styles.spacer} />

      <div className={styles.right}>
        <Dropdown menu={marketplaceMenu} trigger={["click"]} placement="bottomRight">
          <Button className={styles.marketplaceButton}>
            <span className={styles.marketplaceLabel}>{currentMarketplace}</span>
            <DownOutlined className={styles.chevron} />
          </Button>
        </Dropdown>

        <Dropdown menu={profileMenu} trigger={["click"]} placement="bottomRight">
          <Button className={styles.profileButton}>
            <span className={styles.avatar} aria-hidden>
              {username.charAt(0).toUpperCase()}
            </span>
            <span className={styles.username}>{username}</span>
            <DownOutlined className={styles.chevron} />
          </Button>
        </Dropdown>
      </div>
    </header>
  )
}

export default TopBar
